import {
    AppRoute,
    StudioDisplayMode,
    StudioRuntimeName,
    StudioRuntimeStudioVisualSettings,
    StudioSubFeatureName,
    widgetName,
} from "@kaltura-corp/unisphere-genie-studio-types";
import { UnisphereRuntimeLoader, UnisphereRuntimeVisual, UnisphereRuntimeVisualProps } from "@unisphere/runtime-react";
import { useMemo, useState } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { Loader } from "@kaltura/ds-react-components";
import { styled } from "@mui/material";
import { ErrorIllustration, StatusScreen } from "@kaltura/mediaspace-shared-ui";
import { baseUrl, serverUrl, translate } from "@kaltura/mediaspace-shared-utils";
import { AgentStudioHeader } from "./AgentStudioHeader";

export interface AgentStudioProps {
    backUrl: string;
    agentId: string;
    adminKs: string;
    previewKs: string;
}

/**
 * Genie agent studio page for KMS admin.
 *
 * @page-component genie-agent-studio
 */
export const AgentStudio = ({ backUrl, agentId, adminKs, previewKs }: AgentStudioProps) => {
    const {
        application: { environmentName, partnerId },
    } = useConfig();

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const studioVisualSettings = useMemo<StudioRuntimeStudioVisualSettings>(
        () => ({
            envHost: environmentName,
            partnerId: Number(partnerId),
            ks: adminKs,
            contentKs: previewKs, // intentionally same KS as preview
            previewKs,
            analyticsKs: adminKs, // admin KS is good for analytics
            agentId,
            onDirtyChange: setHasChanges,
            siteInfo: {
                siteDomain: serverUrl + baseUrl,
            },
            // Disable media manager - it's not needed in MediaSpace
            features: [
                {
                    featureName: AppRoute.Knowledge,
                    featureConfig: {
                        subFeatures: [
                            {
                                featureName: StudioSubFeatureName.MediaManager,
                                featureConfig: { hidden: true },
                            },
                        ],
                    },
                },
            ],
            displayMode: StudioDisplayMode.Embed,
        }),
        [environmentName, partnerId, agentId, adminKs, previewKs]
    );

    const handleVisualMount: UnisphereRuntimeVisualProps<StudioRuntimeStudioVisualSettings>["onMount"] = ({
        failureReason,
    }) => {
        if (failureReason) {
            console.error("Genie Studio runtime visual failed to mount", { failureReason });
            setHasError(true);
        }
        setIsLoading(false);
    };

    return (
        <>
            <AgentStudioHeader backUrl={backUrl} hasChanges={hasChanges} />

            {isLoading && (
                <StyledStateContainer>
                    <StyledLoaderContainer>
                        <Loader size={"large"} />
                    </StyledLoaderContainer>
                </StyledStateContainer>
            )}

            {hasError && (
                <StatusScreen title={translate("Failed to load agent studio")} image={<ErrorIllustration />} />
            )}

            {!hasError && (
                <>
                    <UnisphereRuntimeLoader
                        widgetName={widgetName}
                        runtimeName={StudioRuntimeName}
                        runtimeSettings={{}}
                    />

                    <UnisphereRuntimeVisual<StudioRuntimeStudioVisualSettings>
                        widgetName={widgetName}
                        runtimeName={StudioRuntimeName}
                        visualType={"studio"}
                        visualSettings={studioVisualSettings}
                        onMount={handleVisualMount}
                    />
                </>
            )}
        </>
    );
};

const StyledStateContainer = styled("div")(({ theme }) => ({
    padding: theme.spacing(5),
    display: "flex",
    justifyContent: "center",
}));

// The kep-ui-kit Loader component is garbage-positioned, so it needs a container of the same size
const StyledLoaderContainer = styled("div")({
    width: 26,
    height: 26,
});
